import { OperatingHour } from "@/types/branch";
import { Category } from "@/types/store";

interface StoreStatus {
  isOpen: boolean;
  reason: "operating-hours" | "manual-closure" | "open";
}

/**
 * Convert a date to the store's timezone
 */
const convertToStoreTime = (date: Date, storeTimezone: string): Date => {
  return new Date(
    date.toLocaleString("en-US", {
      timeZone: storeTimezone,
    }),
  );
};

/**
 * Checks if the store is open based on both operating hours and manual override.
 */
export const checkStoreStatus = (
  operatingHours: OperatingHour[],
  isManuallyOpen: boolean,
  storeTimezone: string,
): StoreStatus => {
  const isWithinOperatingHours = checkOperatingHours(
    operatingHours,
    storeTimezone,
  );

  if (!isWithinOperatingHours) {
    return {
      isOpen: false,
      reason: "operating-hours",
    };
  }

  if (!isManuallyOpen) {
    return {
      isOpen: false,
      reason: "manual-closure",
    };
  }

  return {
    isOpen: true,
    reason: "open",
  };
};

/**
 * Checks if the current time falls within the specified operating hours.
 */
export const checkOperatingHours = (
  operatingHours: OperatingHour[],
  storeTimezone: string,
): boolean => {
  const now = new Date();
  const storeTime = convertToStoreTime(now, storeTimezone);
  const dayOfWeek = storeTime.toLocaleString("en-US", { weekday: "long" });
  const currentTimeInSeconds = timeToSeconds(storeTime);

  return operatingHours.some((hour) => {
    if (hour.day !== dayOfWeek) return false;

    const openTimeInSeconds = timeStringToSeconds(hour.open_time);
    const closeTimeInSeconds = timeStringToSeconds(hour.close_time);

    // Handle overnight hours
    if (closeTimeInSeconds <= openTimeInSeconds) {
      return (
        currentTimeInSeconds >= openTimeInSeconds ||
        currentTimeInSeconds <= closeTimeInSeconds
      );
    }

    return (
      currentTimeInSeconds >= openTimeInSeconds &&
      currentTimeInSeconds <= closeTimeInSeconds
    );
  });
};

/**
 * Calculates the next refresh interval based on operating hours and manual status.
 */
export const calculateNextRefreshInterval = (
  operatingHours: OperatingHour[],
  isManuallyOpen: boolean,
  storeTimezone: string,
): number => {
  const now = new Date();
  const storeTime = convertToStoreTime(now, storeTimezone);
  const currentDayOfWeek = storeTime.toLocaleString("en-US", {
    weekday: "long",
  });
  const currentTimeInSeconds = timeToSeconds(storeTime);

  const upcomingTimes = operatingHours.flatMap((hour) => {
    if (hour.day === currentDayOfWeek) {
      const openTimeInSeconds = timeStringToSeconds(hour.open_time);
      const closeTimeInSeconds = timeStringToSeconds(hour.close_time);

      return [
        { timeInSeconds: openTimeInSeconds, label: "open" },
        { timeInSeconds: closeTimeInSeconds, label: "close" },
      ];
    }
    return [];
  });

  const nextTime = upcomingTimes
    .filter(({ timeInSeconds }) => timeInSeconds > currentTimeInSeconds)
    .sort((a, b) => a.timeInSeconds - b.timeInSeconds)[0];

  const isWithinOperatingHours = checkOperatingHours(
    operatingHours,
    storeTimezone,
  );
  if (isWithinOperatingHours) {
    return 30000; // Poll every 30 seconds during operating hours
  }

  if (nextTime) {
    const secondsUntilNext = nextTime.timeInSeconds - currentTimeInSeconds;
    return secondsUntilNext * 1000;
  }

  return 60000; // Check again in 1 minute
};

// Helper functions remain the same
const timeStringToSeconds = (timeString: string): number => {
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 3600 + minutes * 60;
};

const timeToSeconds = (date: Date): number => {
  return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
};

export const calculateNextCategoryRefreshInterval = (
  categories: Category[],
  storeTimezone: string,
): number => {
  const refreshIntervals = categories.map((category) =>
    calculateNextRefreshInterval(category.category_hours, true, storeTimezone),
  );

  return Math.min(...refreshIntervals, 60000); // Default to 1 minute if no intervals
};
